import { useCallback, useMemo } from "react";
import { Grid, Tag } from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { SlSocialFacebook } from "react-icons/sl";
import { CiLinkedin } from "react-icons/ci";
import { FaXTwitter } from "react-icons/fa6";
import { VITE_API_BASE_URL } from "../../config";
import { DescriptionComponent } from "./DescriptionComponent";
import LogoPlaceholder from "../../assets/logo-placeholder.png";
import { BsPerson } from "react-icons/bs";

const { useBreakpoint } = Grid;

const OverviewTab = ({ institution }) => {
  const screens = useBreakpoint();

  const safeText = (val, dash = "—") =>
    val === null || val === undefined || `${val}`.trim() === "" ? dash : val;

  const addProtocol = useCallback((url) => {
    if (!url) return "";
    const trimmed = `${url}`.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  }, []);

  const isActive = useMemo(() => {
    const v = institution?.membership_status;
    return typeof v === "string" ? v.toLowerCase() === "active" : !!v;
  }, [institution?.membership_status]);

  const items = useMemo(
    () => [
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Category
          </span>
        ),
        value: institution?.category?.name ? (
          <Tag color="magenta">{institution.category.name}</Tag>
        ) : (
          "—"
        ),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Licensing Category
          </span>
        ),
        value: institution?.license_category?.name ? (
          <Tag color="blue">{institution.license_category.name}</Tag>
        ) : (
          "—"
        ),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Ownership Type
          </span>
        ),
        value: institution?.ownership?.name ? (
          <Tag color="cyan">{institution.ownership.name}</Tag>
        ) : (
          "—"
        ),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Code
          </span>
        ),
        value: safeText(institution?.code),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Establishment Year
          </span>
        ),
        value: safeText(institution?.establishment_year),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Region
          </span>
        ),
        value: safeText(institution?.region),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <EnvironmentOutlined /> District
          </span>
        ),
        value: safeText(institution?.location),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <BsPerson /> VC/Principal
          </span>
        ),
        value: safeText(institution?.vc_name),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Focus Areas
          </span>
        ),
        value: (
          <div className="flex flex-wrap gap-1">
            {Array.isArray(institution?.focus_areas) &&
            institution.focus_areas.length > 0 ? (
              institution.focus_areas.map((area) => (
                <Tag key={area.id} color="blue">
                  {area.name}
                </Tag>
              ))
            ) : (
              <span className="text-gray-400 italic">—</span>
            )}
          </div>
        ),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Status
          </span>
        ),
        value: (
          <Tag color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Tag>
        ),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <TagOutlined /> Subscription Expiry Date
          </span>
        ),
        value: safeText(institution?.subscription_expiry_date),
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <MailOutlined /> Email
          </span>
        ),
        value: safeText(institution?.email),
        isLink: true,
        linkType: "mailto",
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <PhoneOutlined /> Contact
          </span>
        ),
        value: safeText(institution?.contact),
        isLink: true,
        linkType: "tel",
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <GlobalOutlined /> Website
          </span>
        ),
        value: safeText(institution?.website_url),
        isLink: true,
        linkType: "",
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <SlSocialFacebook /> Facebook
          </span>
        ),
        value: safeText(institution?.facebook),
        isLink: true,
        linkType: "",
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <CiLinkedin /> LinkedIn
          </span>
        ),
        value: safeText(institution?.linkedin),
        isLink: true,
        linkType: "",
      },
      {
        label: (
          <span className="flex items-center gap-2">
            <FaXTwitter /> Twitter
          </span>
        ),
        value: safeText(institution?.twitter),
        isLink: true,
        linkType: "",
      },
    ],
    [institution, isActive]
  );

  // Normalize link values for DescriptionComponent (add protocol to external URLs only)
  const normalizedItems = useMemo(
    () =>
      items.map((it) => {
        if (!it.isLink || !it.value || it.value === "—") return it;
        if (it.linkType) return it; // mailto/tel kept as-is
        return { ...it, value: addProtocol(it.value) };
      }),
    [items, addProtocol]
  );

  const logoSrc = institution?.logo
    ? `${VITE_API_BASE_URL}/files/logos/${institution.logo}`
    : LogoPlaceholder;

  return (
    <div className="pt-5 pb-4">
      <div className="border border-gray-200 rounded-md p-4">
        <div
          className={`
            grid gap-6 items-start
            ${screens.md ? "grid-cols-12" : "grid-cols-1"}
          `}
        >
          {/* Logo (left on md+) */}
          <div className={screens.md ? "col-span-3" : ""}>
            <div className="w-full max-w-[220px] mx-auto">
              <div className="bg-white rounded-md border border-gray-100 p-3">
                <div className="aspect-[4/3] flex items-center justify-center">
                  <img
                    src={logoSrc}
                    alt={`${institution?.name ?? "Institution"} Logo`}
                    className="max-h-28 w-auto object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className={screens.md ? "col-span-9" : ""}>
            <DescriptionComponent
              items={normalizedItems}
              column={screens.lg ? 2 : 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
